import { useFeatureFlags } from '@brightdrop/feature-flags-client';

/**
 * LaunchDarkly kill-switch that selects the SSE streaming path (when
 * `true`) versus the synchronous POST fallback (when `false`) for the
 * AI Sidekick chat orchestrator. Default in production is `false` —
 * the sync path stays the safe default until UAT signs off SSE
 * end-to-end. Dev / local default is `true` so the team can validate
 * streaming live.
 *
 * Kept distinct from {@link AI_SIDEKICK_FLAG}: that flag gates the
 * panel UI; this flag gates only the network transport. The UI must
 * keep rendering identically whether the orchestrator picks SSE or
 * sync.
 */
export const AI_SIDEKICK_SSE_FLAG = 'SDPCDXP-1499-AI-Sidekick-SSE';

/**
 * Single source of truth for "should the AI Sidekick orchestrator
 * open an SSE stream?".
 *
 * Resolves {@link AI_SIDEKICK_SSE_FLAG} against the user context the
 * consumer's `FeatureFlagsProvider` is initialized with. The
 * orchestrator (`useConversation`) reads this once per render so the
 * code path that runs on the next `sendMessage` reflects the live LD
 * value — no remount required.
 *
 * Strictly returns `true` only when `getFlag` resolves to the boolean
 * literal `true`. Per `FeatureFlagValueType` (defined in
 * `@brightdrop/feature-flags-client`), `getFlag` may resolve to a
 * `string`, `boolean`, `number`, or recursive flag-payload object — so
 * loose coercion would silently enable the SSE branch if an LD admin
 * ever flipped this key from a binary kill-switch to a multivariate
 * config (e.g., a `'control'` variation is truthy). Strict equality
 * enforces the binary contract at the code boundary.
 */
export function useIsSidekickSseEnabled(): boolean {
  const { getFlag } = useFeatureFlags();
  return getFlag(AI_SIDEKICK_SSE_FLAG) === true;
}
